import type { FC } from 'react'
import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider } from 'react-oidc-context'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { AuthCallbackPage, oidcConfig } from '@/infra/auth'
import { UserInitializationProvider } from '@/features/user'
import {
  DaemonSetupPage,
  DaemonUsagePage,
  DocsLayout,
  FaqPage,
  HelpIndexPage,
  OnboardingPage,
  SecurityModelPage,
} from '@/features/docs'
import { SettingsPage } from '@/features/settings'
import { SubmissionStateProvider } from '@/features/events/conversation/controls'
import { CryptoProvider } from '@/infra/crypto'
import { SSEConnectionProvider } from '@/infra/sse'
import { SessionStateProvider } from '@/features/events/conversation/state'
import { AudioNotificationsProvider, StatusProvider, UptimeProvider } from '@/features/status'
import { ThemeProvider } from '@/common/theme'
import { AppLayout } from '@/common/components'
import { DraftsPage, EventsPage } from '@/features/events/pages'
import { SessionHydrator } from '@/features/events/hydration'
import { HomePage } from '@/features/home'
import { env } from '@/infra/config/env'

const EventSimulator = import.meta.env.DEV
  ? (await import('@dev-tools/EventSimulator')).EventSimulator
  : () => null

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: env.QUERY_RETRY_ATTEMPTS,
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, env.QUERY_RETRY_MAX_DELAY_MS),
      staleTime: env.QUERY_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    },
  },
})

export const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SubmissionStateProvider>
        <BrowserRouter>
          <AuthProvider {...oidcConfig}>
            <UserInitializationProvider>
              <CryptoProvider>
                <SSEConnectionProvider>
                  {/* StatusProvider: hydrates host/session status from REST and subscribes to
                      real-time status events dispatched by SSEConnectionProvider. Must be inside
                      SSEConnectionProvider and QueryClientProvider. */}
                  <StatusProvider>
                    <UptimeProvider>
                      <SessionStateProvider>
                        {/* Bootstraps session state from REST before SSE events arrive.
                            Must be inside QueryClientProvider and SessionStateProvider.
                            Intentionally independent of SSEConnectionProvider — both
                            dispatch to the same globalThis event bus. */}
                        <SessionHydrator />
                        <AudioNotificationsProvider>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <ThemeProvider>
                              <AppRoutes />
                              <ReactQueryDevtools initialIsOpen={false} />
                              <EventSimulator />
                            </ThemeProvider>
                          </LocalizationProvider>
                        </AudioNotificationsProvider>
                      </SessionStateProvider>
                    </UptimeProvider>
                  </StatusProvider>
                </SSEConnectionProvider>
              </CryptoProvider>
            </UserInitializationProvider>
          </AuthProvider>
          <ScrollToTop />
        </BrowserRouter>
      </SubmissionStateProvider>
    </QueryClientProvider>
  )
}

/**
 * Scrolls to top on route change, or to the hash element if a fragment is present.
 * Placed inside BrowserRouter so useLocation is available.
 */
const ScrollToTop: FC = () => {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1)
      // Delay to allow the target page to render before scrolling to the element
      const timer = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => {
        clearTimeout(timer)
      }
    }
    window.scrollTo(0, 0)
    return undefined
  }, [pathname, hash])

  return null
}

const AppRoutes: FC = () => {
  return (
    <Routes>
      {/* Public routes - no sidebar */}
      <Route path="/" element={<HomePage />} />
      <Route path="docs" element={<DocsLayout />}>
        <Route index element={<HelpIndexPage />} />
        <Route path="daemon-setup" element={<DaemonSetupPage />} />
        <Route path="daemon-usage" element={<DaemonUsagePage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="security" element={<SecurityModelPage />} />
      </Route>

      {/* Authenticated routes with sidebar */}
      <Route element={<AppLayout />}>
        <Route path="hosts" element={<EventsPage />} />
        <Route path="drafts" element={<DraftsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="auth/callback" element={<AuthCallbackPage />} />

      <Route path="dashboard" element={<Navigate to="/hosts" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
